/** @jsxImportSource @emotion/react */
import { type KeyboardEvent, useCallback } from 'react';

import { MicGlyph } from '@/Client/Icons';

import type { VideoToolboxChatStyles } from './videoToolboxChatStyles';
import { DEFAULT_VIDEO_TOOLBOX_CHAT_STYLES } from './videoToolboxChatStyles';

export type VideoToolboxComposerProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  isListening: boolean;
  isSpeechSupported: boolean;
  disabled?: boolean;
  onToggleListening: () => void;
  onSend: () => void;
  styles?: Pick<
    VideoToolboxChatStyles,
    | 'composer'
    | 'composerInput'
    | 'composerRow'
    | 'talkButton'
    | 'talkButtonLive'
    | 'recDot'
    | 'sendButton'
    | 'composerHint'
  >;
};

/** Footer composer: type a message, dictate into the box, or send to KaiAgent. */
export const VideoToolboxComposer = ({
  draft,
  onDraftChange,
  isListening,
  isSpeechSupported,
  disabled = false,
  onToggleListening,
  onSend,
  styles = DEFAULT_VIDEO_TOOLBOX_CHAT_STYLES,
}: VideoToolboxComposerProps) => {
  const canSend = !disabled && draft.trim().length > 0;

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
    event.preventDefault();
    if (canSend) onSend();
  }, [canSend, onSend]);

  const placeholder = isListening
    ? 'Listening…'
    : 'Walk Kai through this room — say what needs doing and it will scope it…';

  return (
    <div css={styles.composer} className={'v2g-composer'} data-testid={'video-toolbox-composer'}>
      <textarea
        css={styles.composerInput}
        className={'v2g-ci'}
        rows={2}
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={'video-toolbox-composer-input'}
        onChange={event => onDraftChange(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <div css={styles.composerRow} className={'v2g-crow'}>
        <button
          type={'button'}
          css={[styles.talkButton, isListening && styles.talkButtonLive]}
          className={isListening ? 'v2g-cbtn is-live' : 'v2g-cbtn'}
          disabled={disabled || !isSpeechSupported}
          data-testid={'video-toolbox-talk-to-text'}
          onClick={onToggleListening}
        >
          {isListening
            ? (
              <>
                <span css={styles.recDot} className={'v2g-recdot2'} />
                Stop listening
              </>
            )
            : (
              <>
                <MicGlyph />
                Talk to text
              </>
            )}
        </button>
        <button
          type={'button'}
          css={styles.sendButton}
          className={'v2g-send'}
          disabled={!canSend}
          data-testid={'video-toolbox-send'}
          onClick={onSend}
        >
          Send
        </button>
      </div>
      {isListening && (
        <div css={styles.composerHint} className={'v2g-chint'}>
          You can edit the message before sending.
        </div>
      )}
    </div>
  );
};
